import outdent from "outdent";
import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const environments = {
  global: {
    loginDomain: "login.microsoftonline.com",
    graphDomain: "graph.microsoft.com",
  },
  "usgov-l4": {
    loginDomain: "login.microsoftonline.us",
    graphDomain: "graph.microsoft.us",
  },
  "usgov-l5": {
    loginDomain: "login.microsoftonline.us",
    graphDomain: "dod-graph.microsoft.us",
  },
  china: {
    loginDomain: "login.chinacloudapi.cn",
    graphDomain: "microsoftgraph.chinacloudapi.cn",
  },
} as const;
type Environment = keyof typeof environments;

export const microsoftGraph = defineConnector({
  identifier: "microsoftGraph",
  name: "Microsoft Graph",
  tagLine: "Microsoft 365 unified API",
  logo,
  protocol: "http",
  tags: ["it", "productivity", "featured"],
  defaultUrlPattern: () =>
    "^https://graph\\.microsoft(?:china)?\\.(?:com|us|cn)/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Microsoft Graph app",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Microsoft Graph is a unified RESTful programming interface provided by Microsoft. It enables developers to access a wide range of data and functionality across Microsoft services, such as Office 365, Azure Active Directory, OneDrive, Outlook, SharePoint, Teams, and more.

            1. Navigate to your [Azure app registrations](https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade) and select **New Registration**
            2. **Name**: Give your app a descriptive name
            3. **Supported account types**: Select 'Single tenant only'
            4. **Redirect URI**: Leave this blank – it's not needed here
            5. Select **Register**
          `,
        },
      ],
    },
    {
      title: "Get client ID and tenant ID",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Navigate to the **Overview** of your created app and copy the following values:
          `,
        },
        {
          kind: "input",
          label: "Application (client) ID",
          target: { kind: "oauthConnection", field: "clientId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
        {
          kind: "input",
          label: "Directory (tenant) ID",
          target: { kind: "configuration", field: "tenantId" },
          placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        },
      ],
    },
    {
      title: "Create client secret",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Select **Add a certificate or secret** from the overview page, then select **+ New client secret**
            2. Enter a description for the client secret and select your preferred expires setting
            3. Select **Add**
            4. Copy the **value** of the secret
          `,
        },
        {
          kind: "input",
          label: "Client secret value",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Add API permissions",
      items: [
        {
          kind: "markdown",
          content: outdent`
            1. Navigate to **API permissions** and select **+ Add a permission**
            2. Select the **Microsoft Graph** API
            3. Select **Application permissions** (not delegated)
            4. Select your desired permissions and select **Add permissions**

            Some permissions may require admin consent before they take effect.
          `,
        },
      ],
    },
    {
      title: "Specify your environment",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Microsoft has different domains for different deployments. [Learn more](https://learn.microsoft.com/en-us/graph/deployments#microsoft-graph-and-graph-explorer-service-root-endpoints)
          `,
        },
        {
          kind: "select",
          label: "Environment",
          target: { kind: "configuration", field: "environment" },
          options: [
            {
              label: "Microsoft Graph global service",
              value: "global",
            },
            {
              label: "Microsoft Graph for US Government L4",
              value: "usgov-l4",
            },
            {
              label: "Microsoft Graph for US Government L5 (DOD)",
              value: "usgov-l5",
            },
            {
              label: "Microsoft Graph China operated by 21Vianet",
              value: "china",
            },
          ],
          defaultValue: "global",
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "client_credentials",
    tokenEndpoint: ({ environment, tenantId }) => {
      const env = (environment as Environment) || "global";
      const config = environments[env];
      return `https://${config.loginDomain}/${tenantId}/oauth2/v2.0/token`;
    },
    scopes: ({ environment }) => {
      const env = (environment as Environment) || "global";
      const config = environments[env];
      return [`https://${config.graphDomain}/.default`];
    },
    clientCredentialsFrom: "db",
    tokenEndpointAuthMethod: () => "client_secret_post",
  },
  connectionTest: {
    protocol: "http",
    request: () => ({
      url: "https://graph.microsoft.com/v1.0/organization",
      expectedStatus: [200, 403],
    }),
  },
});
